import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { ArrowLeft, Mail, Lock, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { LanguageSelector, CurrencySelector } from '../../components/LocaleSelectors';
import { useAuth } from '../../context/AuthContext';

export default function StaffLogin() {
  const { t, isRTL } = useLanguage();
  const { staffLoginWithCredentials } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await staffLoginWithCredentials(email, password);
      navigate('/staff/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-deep-charcoal flex items-center justify-center px-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="w-full max-w-md">
        {/* Locale Selectors */}
        <div className={`flex justify-end gap-2 mb-4 ${isRTL ? 'justify-start' : ''}`}>
          <LanguageSelector className="bg-white/10 border-white/20 text-white" />
          <CurrencySelector className="bg-white/10 border-white/20 text-white" />
        </div>

        {/* Back Link */}
        <Link 
          to="/" 
          className={`inline-flex items-center text-white/60 hover:text-sage font-body text-sm mb-8 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
          data-testid="back-to-home"
        >
          <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
          {t('home')}
        </Link>

        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-sage">FTASM</h1>
          <p className="font-body text-white/60 mt-2">{t('staffPortal')}</p>
        </div>

        {/* Login Card */}
        <Card className="bg-white/5 rounded-3xl shadow-soft border border-white/10 backdrop-blur-xl">
          <CardContent className="p-8">
            <h2 className="font-heading text-2xl font-semibold text-white mb-2">
              {t('staffSignIn')}
            </h2>
            <p className="font-body text-white/60 mb-8">
              {t('staffAccessDesc')}
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="font-body text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label className="font-body text-sm text-white/80">{t('emailAddress')}</label>
                <div className="relative">
                  <Mail className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <Input
                    type="email"
                    placeholder="staff@ftasm.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={`h-14 bg-white/5 border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-sage ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
                    data-testid="staff-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="font-body text-sm text-white/80">{t('password')}</label>
                <div className="relative">
                  <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={`h-14 bg-white/5 border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-sage ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'}`}
                    data-testid="staff-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl h-14 bg-sage text-deep-charcoal hover:bg-sage/90 font-body font-medium"
                data-testid="staff-login-btn"
              >
                {loading ? t('signingIn') : t('signIn')}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="font-body text-xs text-white/40">
                {t('onlyAuthorizedStaff')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Customer Portal Link */}
        <div className="mt-6 text-center">
          <Link 
            to="/login" 
            className="font-body text-sm text-white/50 hover:text-sage transition-colors"
            data-testid="customer-portal-link"
          >
            ← {t('customerPortal')}
          </Link>
        </div>
      </div>
    </div>
  );
}
